import { useState, useEffect, useCallback } from 'react';

interface StreakData {
  currentStreak: number;
  longestStreak: number;
  lastPlayedDate: string | null;
  gamesPlayedToday: number;
  totalGamesPlayed: number;
  totalWins: number;
}

interface DailyChallenge {
  id: string;
  title: string;
  description: string;
  target: number;
  progress: number;
  reward: number;
  completed: boolean;
}

const STORAGE_KEY = '67chaos-streaks';
const CHALLENGES_KEY = '67chaos-daily-challenges';

const DAILY_CHALLENGES: Omit<DailyChallenge, 'progress' | 'completed'>[] = [
  { id: 'play_3', title: 'Triple Play', description: 'Play 3 games today', target: 3, reward: 50 },
  { id: 'win_1', title: 'Victory!', description: 'Win a game', target: 1, reward: 75 },
  { id: 'win_2', title: 'Winning Streak', description: 'Win 2 games today', target: 2, reward: 150 },
  { id: 'play_5', title: 'Marathon', description: 'Play 5 games today', target: 5, reward: 100 },
  { id: 'perfect', title: 'Perfect Game', description: 'Win in under 5 rounds', target: 1, reward: 200 },
];

function getToday(): string {
  return new Date().toISOString().split('T')[0];
}

function getDefaultStreakData(): StreakData {
  return {
    currentStreak: 0,
    longestStreak: 0,
    lastPlayedDate: null,
    gamesPlayedToday: 0,
    totalGamesPlayed: 0,
    totalWins: 0,
  };
}

function getDailyChallenges(date: string): DailyChallenge[] {
  // Use date as seed for consistent daily challenges
  const dateHash = date.split('-').reduce((acc, part) => acc + parseInt(part), 0);
  const shuffled = [...DAILY_CHALLENGES].sort((a, b) => {
    const aHash = (a.id.charCodeAt(0) + dateHash) % 100;
    const bHash = (b.id.charCodeAt(0) + dateHash) % 100;
    return aHash - bHash;
  });
  
  // Return 3 challenges for today
  return shuffled.slice(0, 3).map(c => ({
    ...c,
    progress: 0,
    completed: false,
  }));
}

export function useStreaks() {
  const [streakData, setStreakData] = useState<StreakData>(getDefaultStreakData);
  const [dailyChallenges, setDailyChallenges] = useState<DailyChallenge[]>([]);

  // Load data on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const data = JSON.parse(stored) as StreakData;
        const today = getToday();
        
        // Check if we need to reset daily count
        if (data.lastPlayedDate !== today) {
          // Check if streak should continue or reset
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);
          const yesterdayStr = yesterday.toISOString().split('T')[0];
          
          if (data.lastPlayedDate !== yesterdayStr && data.lastPlayedDate !== today) {
            // Streak broken
            data.currentStreak = 0;
          }
          data.gamesPlayedToday = 0;
        }
        
        setStreakData(data);
      } catch {
        setStreakData(getDefaultStreakData());
      }
    }

    // Load or generate daily challenges
    const challengesStored = localStorage.getItem(CHALLENGES_KEY);
    const today = getToday();
    
    if (challengesStored) {
      try {
        const { date, challenges } = JSON.parse(challengesStored);
        if (date === today) {
          setDailyChallenges(challenges);
          return;
        }
      } catch {
        // Generate new challenges
      }
    }
    
    // Generate new challenges for today
    const newChallenges = getDailyChallenges(today);
    setDailyChallenges(newChallenges);
    localStorage.setItem(CHALLENGES_KEY, JSON.stringify({ date: today, challenges: newChallenges }));
  }, []);

  // Save data when it changes
  useEffect(() => {
    if (streakData.totalGamesPlayed > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(streakData));
    }
  }, [streakData]);

  // Save challenges when they change
  useEffect(() => {
    if (dailyChallenges.length > 0) {
      localStorage.setItem(CHALLENGES_KEY, JSON.stringify({ 
        date: getToday(), 
        challenges: dailyChallenges 
      }));
    }
  }, [dailyChallenges]);

  const recordGame = useCallback((won: boolean, rounds: number) => {
    const today = getToday();
    
    setStreakData(prev => {
      const isNewDay = prev.lastPlayedDate !== today;
      let newStreak = prev.currentStreak;
      
      if (isNewDay) {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().split('T')[0];
        
        if (prev.lastPlayedDate === yesterdayStr) {
          // Continue streak
          newStreak = prev.currentStreak + 1;
        } else if (prev.lastPlayedDate === null || prev.lastPlayedDate !== today) {
          // Start new streak
          newStreak = 1;
        }
      }
      
      const newData: StreakData = {
        currentStreak: newStreak,
        longestStreak: Math.max(prev.longestStreak, newStreak),
        lastPlayedDate: today,
        gamesPlayedToday: isNewDay ? 1 : prev.gamesPlayedToday + 1,
        totalGamesPlayed: prev.totalGamesPlayed + 1,
        totalWins: prev.totalWins + (won ? 1 : 0),
      };
      
      return newData;
    });

    // Update challenge progress
    setDailyChallenges(prev => prev.map(challenge => {
      if (challenge.completed) return challenge;
      
      let newProgress = challenge.progress;
      
      switch (challenge.id) {
        case 'play_3':
        case 'play_5':
          newProgress = challenge.progress + 1;
          break;
        case 'win_1':
        case 'win_2':
          if (won) newProgress = challenge.progress + 1;
          break;
        case 'perfect':
          if (won && rounds <= 5) newProgress = 1;
          break;
      }
      
      return {
        ...challenge,
        progress: newProgress,
        completed: newProgress >= challenge.target,
      };
    }));
  }, []);

  const getCompletedChallengeRewards = useCallback((): number => {
    return dailyChallenges
      .filter(c => c.completed)
      .reduce((sum, c) => sum + c.reward, 0);
  }, [dailyChallenges]);

  return {
    streakData,
    dailyChallenges,
    recordGame,
    getCompletedChallengeRewards,
  };
}